import React from 'react';
import {
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableFooter,
    TablePagination,
    Paper,
    Box,
    Typography,
    useTheme,
    useMediaQuery,
    IconButton,
    Button,
    Link,
    CircularProgress,
    Alert,
    Snackbar,
    Tooltip
} from '@mui/material';
import { FirstPage, KeyboardArrowLeft, KeyboardArrowRight, LastPage } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../utils/auth/msalConfig';
import { chatService } from '../services';

// Helper function to check if a string is a URL
const isUrl = (str) => {
    try {
        // Simple URL pattern - can be expanded for more complex URL validation
        const pattern = /^(https?:\/\/|www\.)[^\s$.?#].[^\s]*$/i;
        return pattern.test(str);
    } catch (e) {
        return false;
    }
};

// Component to render content as link if it's a URL or HTML anchor tag
const CellContent = ({ content, column, color }) => {
    // Check if content contains HTML anchor tag
    if (typeof content === 'string' && content.includes('<a ')) {
        // Parse HTML anchor tag with more flexible regex to handle various attributes
        // Matches: <a href="url" target="_blank">text</a> or <a href="url">text</a>
        const anchorMatch = content.match(/<a\s[^>]*href\s*=\s*["']([^"']*)["'][^>]*>([^<]*)<\/a>/i);
        
        if (anchorMatch) {
            const url = anchorMatch[1];
            const displayText = anchorMatch[2];
            
            return (
                <Link 
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ 
                        color: color || 'primary.main',
                        fontWeight: 500,
                        '&:hover': {
                            color: color || 'primary.dark'
                        }
                    }}
                >
                    {displayText}
                </Link>
            );
        }
        
        // If HTML detected but couldn't parse, strip HTML tags and show as text
        const strippedContent = content.replace(/<[^>]*>/g, '');
        return strippedContent;
    }
    
    // Check if content is a URL string
    if (typeof content === 'string' && isUrl(content)) {
        // Check if the URL contains a display text pattern [text](url)
        const displayTextMatch = content.match(/\[(.*?)\]\((https?:\/\/.*?|www\..*?)\)/i);
        
        if (displayTextMatch) {
            // Extract display text and actual URL
            const displayText = displayTextMatch[1];
            const url = displayTextMatch[2];
            
            return (
                <Link 
                    href={url.startsWith('www.') ? `https://${url}` : url}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: color || 'primary.main' }}
                >
                    {displayText}
                </Link>
            );
        } else {
            // Regular URL without display text
            const href = content.startsWith('www.') ? `https://${content}` : content;
            
            return (
                <Link 
                    href={href}
                    target="_blank"
                    rel="noopener noreferrer"
                    underline="hover"
                    sx={{ color: color || 'primary.main' }}
                >
                    {content}
                </Link>
            );
        }
    }
    
    // Return regular content if not a URL or HTML anchor
    return content;
};

// Helper function to parse hierarchical column structure
const parseHierarchicalColumns = (columns) => {
    const groups = {};
    const flatColumns = [];
    
    columns.forEach(column => {
        if (column.includes(' | ')) {
            const [groupName, subColumn] = column.split(' | ');
            if (!groups[groupName]) {
                groups[groupName] = [];
            }
            groups[groupName].push({
                originalName: column,
                displayName: subColumn,
                groupName: groupName
            });
        } else {
            // Standalone column (no pipe separator)
            flatColumns.push({
                originalName: column,
                displayName: column,
                groupName: null
            });
        }
    });
    
    return { groups, flatColumns };
};

function TablePaginationActions(props) {
    const theme = useTheme();
    const { count, page, rowsPerPage, onPageChange } = props;

    const handleFirstPageButtonClick = event => onPageChange(event, 0);
    const handleBackButtonClick = event => onPageChange(event, page - 1);
    const handleNextButtonClick = event => onPageChange(event, page + 1);
    const handleLastPageButtonClick = event => onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));

    return (
        <Box sx={{ flexShrink: 0, ml: 2.5, display: 'flex', gap: 1 }}>
            <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label="first page">
                {theme.direction === 'rtl' ? <LastPage /> : <FirstPage />}
            </IconButton>
            <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label="previous page">
                {theme.direction === 'rtl' ? <KeyboardArrowRight /> : <KeyboardArrowLeft />}
            </IconButton>
            <IconButton onClick={handleNextButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="next page">
                {theme.direction === 'rtl' ? <KeyboardArrowLeft /> : <KeyboardArrowRight />}
            </IconButton>
            <IconButton onClick={handleLastPageButtonClick} disabled={page >= Math.ceil(count / rowsPerPage) - 1} aria-label="last page">
                {theme.direction === 'rtl' ? <FirstPage /> : <LastPage />}
            </IconButton>
        </Box>
    );
}

export default function RenderTable({ tableData, sessionId, questionId }) {
    const theme = useTheme();
    const isNonDesktop = useMediaQuery(theme.breakpoints.down('lg'));
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(5);
    const [isFullscreen, setIsFullscreen] = React.useState(false);
    const [downloadLoading, setDownloadLoading] = React.useState(false);
    const [downloadError, setDownloadError] = React.useState(null);
    const [downloadSuccess, setDownloadSuccess] = React.useState(false);
    const [columnWidths, setColumnWidths] = React.useState([]);
      const { instance, accounts } = useMsal();
    const isAuthenticated = accounts.length > 0;

    // Store original data length before limiting
    const originalDataLength = tableData ? tableData.length : 0;
      // Use API data if provided, otherwise fallback to empty array
    // Limit to maximum 100 rows for display performance
    const rows = React.useMemo(() => 
        tableData ? tableData.slice(0, 100) : [], 
        [tableData]
    );
    
    // Get column names from the first row - memoized to prevent infinite re-renders
    const columns = React.useMemo(() => 
        rows.length > 0 ? Object.keys(rows[0]) : [], 
        [rows]
    );

    // Parse hierarchical structure
    const { groups, flatColumns } = React.useMemo(() => 
        parseHierarchicalColumns(columns), 
        [columns]
    );

    // Calculate dynamic widths for the first two columns
    React.useEffect(() => {
        if (rows.length > 0 && columns.length > 0) {
            const widths = [];
            
            // Calculate width for first column with equal base width
            const firstColumnValues = [columns[0], ...rows.map(row => String(row[columns[0]] || ''))];
            const firstColumnMaxLength = Math.max(...firstColumnValues.map(val => val.length));
            const firstColumnWidth = Math.min(Math.max(firstColumnMaxLength * 10, 200), 250); // Min 200px, Max 250px, balanced multiplier
            widths[0] = firstColumnWidth;

            // Calculate width for second column with equal base width
            if (columns.length > 1) {
                const secondColumnValues = [columns[1], ...rows.map(row => String(row[columns[1]] || ''))];
                const secondColumnMaxLength = Math.max(...secondColumnValues.map(val => val.length));
                const secondColumnWidth = Math.min(Math.max(secondColumnMaxLength * 10, 200), 250); // Min 200px, Max 250px, balanced multiplier
                widths[1] = secondColumnWidth;
            }

            setColumnWidths(widths);
        }
    }, [rows, columns]);

    // Prepare visible rows for pagination
    const visibleRows = rowsPerPage > 0
        ? rows?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
        : rows;

    // Get authentication token for API calls with enhanced error handling
const getAuthToken = async () => {
        if (accounts.length === 0) {
            throw new Error('No authenticated accounts found');
        }

        try {
            console.log('Attempting silent token acquisition...');
            const response = await instance.acquireTokenSilent({
                ...loginRequest,
                account: accounts[0]
            });
            console.log('Silent token acquisition successful');
            return response.accessToken;
        } catch (error) {
            console.log("Silent token acquisition failed:", error.message);
            
            // Check if it's an interaction required error
            if (error.errorCode === 'interaction_required' || 
                error.errorCode === 'consent_required' || 
                error.errorCode === 'login_required') {
                
                try {
                    console.log('Attempting interactive token acquisition...');
                    const response = await instance.acquireTokenPopup(loginRequest);
                    console.log('Interactive token acquisition successful');
                    return response.accessToken;
                } catch (interactiveError) {
                    console.error("Interactive token acquisition failed:", interactiveError);
                    throw new Error(`Authentication failed: ${interactiveError.message || 'Please try signing in again'}`);
                }
            } else {
                console.error("Token acquisition failed:", error);
                throw new Error(`Authentication error: ${error.message || 'Unable to authenticate user'}`);
            }
        }
    };// API Excel download handler - no fallback
    const downloadExcelFromAPI = async () => {
        // Input validation
        if (!sessionId || !questionId) {
            setDownloadError('Session ID and Question ID are required for download. Please try again with a valid query.');
            return;
        }

        if (!isAuthenticated) {
            setDownloadError('You must be logged in to download Excel files. Please sign in and try again.');
            return;
        }

        // Check if there's data to download
        if (!rows || rows.length === 0) {
            setDownloadError('No data available to download. Please run a query first.');
            return;
        }

        setDownloadLoading(true);
        setDownloadError(null);
        setDownloadSuccess(false);

        try {
            console.log('Starting Excel download...', { sessionId, questionId });
            
            // Get authentication token with timeout
            const token = await Promise.race([
                getAuthToken(),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Authentication timeout')), 10000)
                )
            ]);

            if (!token) {
                throw new Error('Unable to obtain authentication token. Please sign in again.');
            }

            console.log('Token acquired, calling download API...');

            // Call the download API with timeout
            const result = await Promise.race([
                chatService.downloadExcel({
                    sessionId,
                    questionId,
                    token
                }),
                new Promise((_, reject) => 
                    setTimeout(() => reject(new Error('Download request timed out. Please try again.')), 30000)
                )
            ]);

            if (!result.success) {
                throw new Error(result.error || 'Server failed to generate Excel file');
            }

            if (!result.response) {
                throw new Error('Invalid response from server');
            }            console.log('Download API responded successfully');

            // Check if response contains data field (base64 encoded Excel)
            if (result.response && typeof result.response === 'object' && result.response.data) {
                console.log('API returned JSON response with base64 data');
                
                // Decode base64 data to blob
                try {
                    const base64Data = result.response.data;
                    const binaryString = atob(base64Data);
                    const bytes = new Uint8Array(binaryString.length);
                    for (let i = 0; i < binaryString.length; i++) {
                        bytes[i] = binaryString.charCodeAt(i);
                    }
                    const blob = new Blob([bytes], { 
                        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
                    });
                    
                    if (!blob || blob.size === 0) {
                        throw new Error('Decoded file is empty');
                    }
                    
                    console.log('Base64 data decoded successfully, blob size:', blob.size);
                    
                    // Create download link
                    const downloadUrl = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    
                    // Use filename from API response or generate default
                    let filename = result.response.filename || `parts360_data_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`; // Changed from .xlsx to .csv since xlsx library not used
                    
                    link.download = filename;
                    link.style.display = 'none';
                    document.body.appendChild(link);
                    
                    // Trigger download
                    try {
                        link.click();
                        console.log('Download triggered successfully with filename:', filename);
                    } catch (clickError) {
                        console.error('Error triggering download:', clickError);
                        throw new Error('Failed to start download. Please try again.');
                    } finally {
                        document.body.removeChild(link);
                        window.URL.revokeObjectURL(downloadUrl);
                    }
                    
                } catch (decodeError) {
                    console.error('Error decoding base64 data:', decodeError);
                    throw new Error('Failed to process downloaded file data');
                }
                
            } else {
                // Handle direct blob response (original format)
                console.log('API returned direct blob response');
                
                // Validate response content type
                const contentType = result.contentType || result.response.headers?.get('content-type') || '';
                // Commented out xlsx validation since xlsx library not used
                // if (!contentType.includes('application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') && 
                //     !contentType.includes('application/octet-stream')) {
                //     console.warn('Unexpected content type:', contentType);
                //     throw new Error('Server did not return a valid Excel file. Please contact support.');
                // }

                // Get the file as blob with error handling
                let blob;
                try {
                    blob = await result.response.blob();
                    if (!blob || blob.size === 0) {
                        throw new Error('Downloaded file is empty');
                    }
                } catch (blobError) {
                    console.error('Error creating blob:', blobError);
                    throw new Error('Failed to process downloaded file');
                }

                console.log('File blob created successfully, size:', blob.size);
                
                // Create download link
                const downloadUrl = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = downloadUrl;
                
                // Get filename from Content-Disposition header or use default with timestamp
                let filename = `parts360_data_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.csv`; // Changed from .xlsx to .csv since xlsx library not used
                
                if (result.contentDisposition) {
                    const filenameMatch = result.contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
                    if (filenameMatch && filenameMatch[1]) {
                        filename = filenameMatch[1].replace(/['"]/g, '');
                    }
                }
                
                link.download = filename;
                link.style.display = 'none';
                document.body.appendChild(link);
                
                // Trigger download
                try {
                    link.click();
                    console.log('Download triggered successfully');
                } catch (clickError) {
                    console.error('Error triggering download:', clickError);
                    throw new Error('Failed to start download. Please try again.');
                } finally {
                    document.body.removeChild(link);
                    window.URL.revokeObjectURL(downloadUrl);
                }
            }
            
            setDownloadSuccess(true);
            console.log('Excel file downloaded successfully from API');

        } catch (error) {
            console.error('Excel download failed:', error);
            
            // Provide specific error messages based on error type
            let errorMessage = 'Failed to download Excel file';
            
            if (error.message.includes('timeout') || error.message.includes('Timeout')) {
                errorMessage = 'Download request timed out. Please check your connection and try again.';
            } else if (error.message.includes('401') || error.message.includes('Unauthorized')) {
                errorMessage = 'Authentication failed. Please sign in again and try downloading.';
            } else if (error.message.includes('403') || error.message.includes('Forbidden')) {
                errorMessage = 'You do not have permission to download this file.';
            } else if (error.message.includes('404') || error.message.includes('Not Found')) {
                errorMessage = 'The requested data was not found. Please run a new query.';
            } else if (error.message.includes('500') || error.message.includes('Internal Server Error')) {
                errorMessage = 'Server error occurred. Please try again later or contact support.';
            } else if (error.message.includes('network') || error.message.includes('fetch')) {
                errorMessage = 'Network error. Please check your internet connection and try again.';
            } else if (error.message) {
                errorMessage = error.message;
            }
            
            setDownloadError(errorMessage);
        } finally {
            setDownloadLoading(false);
        }
    };

    const handleChangePage = (event, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleDownload = () => {
        downloadExcelFromAPI();
    };

    const handleCloseSuccess = () => setDownloadSuccess(false);
    const handleCloseError = () => setDownloadError(null);

    const paperSx = isFullscreen
        ? {
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100vw',
            height: '100vh',
            zIndex: theme.zIndex.modal,
            m: 0,
            p: 0,
            borderRadius: 0,
        }
        : {
            borderRadius: 3,
            boxShadow: 3,
            mt: 2,
            width: '100%',
        };

    const toggleFullscreen = () => setIsFullscreen(f => !f);

    // No rows to display
    if (rows.length === 0) {
        return (
            <>
            </>
        );
    }

    // Check if we have hierarchical data
    const hasHierarchicalData = Object.keys(groups).length > 0;

    return (
        <Paper elevation={0} sx={paperSx}>
            <Box sx={{ bgcolor: 'background.paper', p: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderRadius: 3 }}>
                <Typography variant="h6" sx={{ fontSize: '14px' }}>
                    Query Results 
                    {originalDataLength > 100 
                        ? ` (Showing first 100 of ${originalDataLength} rows - Download Excel for complete data)`
                        : ` (${rows.length} rows)`
                    }
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Tooltip 
                        title={
                            downloadLoading 
                                ? "Downloading Excel file..."
                                : rows.length === 0 
                                    ? "No data to download" 
                                    : !sessionId || !questionId 
                                        ? "Session ID and Question ID required for download"
                                        : !isAuthenticated
                                            ? "Please sign in to download files"
                                            : "Download Excel"
                        } 
                        arrow
                    >
                        <span>
                            <IconButton 
                                size="small" 
                                onClick={handleDownload} 
                                disabled={downloadLoading || rows.length === 0 || !sessionId || !questionId || !isAuthenticated}
                                sx={{ 
                                    backgroundColor: downloadLoading ? 'action.hover' : 'background.paper', 
                                    borderRadius: 1, 
                                    '&:hover': { backgroundColor: 'action.hover' },
                                    '&:disabled': { 
                                        opacity: 0.6,
                                        cursor: downloadLoading ? 'wait' : 'not-allowed'
                                    },
                                    transition: 'all 0.2s ease-in-out'
                                }}
                                aria-label={downloadLoading ? "Downloading..." : "Download Excel file"}
                            >
                                {downloadLoading ? (
                                    <CircularProgress 
                                        size={20} 
                                        color="primary"
                                        thickness={4}
                                        sx={{ 
                                            animation: 'spin 1s linear infinite',
                                            '@keyframes spin': {
                                                '0%': { transform: 'rotate(0deg)' },
                                                '100%': { transform: 'rotate(360deg)' }
                                            }
                                        }}
                                    />
                                ) : (
                                    <Icon 
                                        icon="fa:download" 
                                        width="20" 
                                        height="20"
                                        style={{
                                            color: (!sessionId || !questionId || !isAuthenticated || rows.length === 0) 
                                                ? 'rgba(0,0,0,0.3)' 
                                                : undefined
                                        }}
                                    />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                    <IconButton size="small" onClick={toggleFullscreen} sx={{ backgroundColor: 'background.paper', borderRadius: 1, '&:hover': { backgroundColor: 'action.hover' } }}>
                        {isFullscreen ? <Icon icon="garden:minimize-stroke-12" width="20" height="20" /> : <Icon icon="mingcute:fullscreen-fill" width="20" height="20" />}
                    </IconButton>
                </Box>
            </Box>

            {/* Scrollable table container */}
            <TableContainer sx={{
                maxHeight: isFullscreen ? 'calc(100vh - 96px)' : 300,
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '4px',
                    height: '4px'
                },
                '&::-webkit-scrollbar-track': {
                    background: 'transparent'
                },
                '&::-webkit-scrollbar-thumb': {
                    background: '#bdbdbd',
                    borderRadius: '4px'
                },
                '&::-webkit-scrollbar-thumb:hover': {
                    background: '#9e9e9e'
                },
                scrollbarWidth: 'thin',
                scrollbarColor: '#bdbdbd transparent'
            }}>
                <Table stickyHeader size="small" aria-label="query results table">
                    <TableHead sx={{ backgroundColor: 'background.default', zIndex: 1 }}>
                        {hasHierarchicalData ? (
                            // Hierarchical header structure
                            <>
                                {/* Group headers row */}
                                <TableRow>
                                    {flatColumns.map((col, index) => (
                                        <TableCell
                                            key={`flat-${index}`}
                                            colSpan={1}
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: 'text.primary',
                                                backgroundColor: 'white',
                                                textAlign: 'center',
                                                position: index < 2 && !isNonDesktop ? 'sticky' : 'static',
                                                left: (index < 2 && !isNonDesktop) ? (index === 0 ? 0 : `${columnWidths[0] || 200}px`) : 'auto',
                                                width: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                minWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                maxWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'none',
                                                zIndex: (index < 2 && !isNonDesktop) ? 10 : 1,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            
                                            {/* Empty for flat columns in group header row */}
                                        </TableCell>
                                    ))}
                                    {Object.entries(groups).map(([groupName, groupColumns], groupIndex) => (
                                        <TableCell
                                            key={`group-${groupName}`}
                                            colSpan={groupColumns.length}
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 700,
                                                color: 'white',
                                                backgroundColor: groupIndex === 0 ? theme.palette.primary.light : 
                                                              groupIndex === 1 ? theme.palette.secondary.light :
                                                              groupIndex === 2 ? theme.palette.success.light :
                                                              groupIndex === 3 ? theme.palette.warning.light :
                                                              theme.palette.info.light,
                                                textAlign: 'center',
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap',
                                                zIndex: 1
                                            }}
                                        >
                                            <CellContent content={groupName} column={groupName} color="white" />
                                        </TableCell>
                                    ))}
                                </TableRow>
                                {/* Sub-column headers row */}
                                <TableRow>
                                    {flatColumns.map((col, index) => (
                                        <TableCell
                                            key={`sub-flat-${index}`}
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                position: index < 2 && !isNonDesktop ? 'sticky' : 'static',
                                                left: (index < 2 && !isNonDesktop) ? (index === 0 ? 0 : `${columnWidths[0] || 200}px`) : 'auto',
                                                width: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                minWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                maxWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'none',
                                                zIndex: (index < 2 && !isNonDesktop) ? 10 : 1,
                                                backgroundColor: theme.palette.grey[100],
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <CellContent content={col.displayName} column={col.originalName} />
                                        </TableCell>
                                    ))}
                                    {Object.entries(groups).map(([groupName, groupColumns], groupIndex) => 
                                        groupColumns.map((col, index) => (
                                            <TableCell
                                                key={`sub-${groupName}-${index}`}
                                                sx={{
                                                    fontSize: '0.75rem',
                                                    fontWeight: 600,
                                                    color: 'text.secondary',
                                                    backgroundColor: theme.palette.grey[100],
                                                    borderRight: `1px solid ${theme.palette.divider}`,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <CellContent content={col.displayName} column={col.originalName} />
                                            </TableCell>
                                        ))
                                    )}
                                </TableRow>
                            </>
                        ) : (
                            // Standard single header row
                        <TableRow>
                                                                {columns.map((column, index) => (
                                        <TableCell
                                            key={index}
                                            sx={{
                                                fontSize: '0.75rem',
                                                fontWeight: 600,
                                                color: 'text.secondary',
                                                position: index < 2 && !isNonDesktop ? 'sticky' : 'static',
                                                left: (index < 2 && !isNonDesktop) ? (index === 0 ? 0 : `${columnWidths[0] || 200}px`) : 'auto',
                                                width: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                minWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'auto',
                                                maxWidth: (index < 2 && !isNonDesktop) ? `${columnWidths[index] || 200}px` : 'none',
                                                zIndex: (index < 2 && !isNonDesktop) ? 3 : 1,
                                                backgroundColor: theme.palette.background.default,
                                                borderRight: `1px solid ${theme.palette.divider}`,
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            <CellContent content={column} column={column} />
                                        </TableCell>
                                    ))}
                        </TableRow>
                        )}
                    </TableHead>

                    <TableBody>
                        {visibleRows.map((row, rowIndex) => (
                            <TableRow
                                key={rowIndex}
                                sx={{ 
                                    backgroundColor: rowIndex % 2 === 0 ? 'background.paper' : 'background.default', 
                                    '&:hover': { backgroundColor: 'action.hover' } 
                                }}
                            >
                                {hasHierarchicalData ? (
                                    <>
                                        {/* Flat columns */}
                                        {flatColumns.map((col, colIndex) => (
                                            <TableCell
                                                key={`flat-cell-${colIndex}`}
                                                sx={{
                                                    fontSize: '0.7rem',
                                                    color: 'text.primary',
                                                    position: colIndex < 2 && !isNonDesktop ? 'sticky' : 'static',
                                                    left: (colIndex < 2 && !isNonDesktop) ? (colIndex === 0 ? 0 : `${columnWidths[0] || 200}px`) : 'auto',
                                                    width: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'auto',
                                                    minWidth: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'auto',
                                                    maxWidth: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'none',
                                                    zIndex: (colIndex < 2 && !isNonDesktop) ? 1 : 0,
                                                    backgroundColor: rowIndex % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                                                    borderRight: `1px solid ${theme.palette.divider}`,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap'
                                                }}
                                            >
                                                <CellContent content={row[col.originalName]} column={col.originalName} />
                                            </TableCell>
                                        ))}
                                        {/* Grouped columns */}
                                        {Object.entries(groups).map(([groupName, groupColumns]) => 
                                            groupColumns.map((col, colIndex) => (
                                                <TableCell
                                                    key={`group-cell-${groupName}-${colIndex}`}
                                                    sx={{
                                                        fontSize: '0.7rem',
                                                        color: 'text.primary',
                                                        backgroundColor: rowIndex % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                                                        borderRight: `1px solid ${theme.palette.divider}`,
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}
                                                >
                                                    <CellContent content={row[col.originalName]} column={col.originalName} />
                                                </TableCell>
                                            ))
                                        )}
                                    </>
                                ) : (
                                    // Standard flat structure
                                    columns.map((column, colIndex) => (
                                    <TableCell
                                            key={colIndex}
                                            sx={{
                                                fontSize: '0.7rem',
                                            color: 'text.primary',
                                            position: colIndex < 2 && !isNonDesktop ? 'sticky' : 'static',
                                            left: (colIndex < 2 && !isNonDesktop) ? (colIndex === 0 ? 0 : `${columnWidths[0] || 200}px`) : 'auto',
                                            width: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'auto',
                                            minWidth: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'auto',
                                            maxWidth: (colIndex < 2 && !isNonDesktop) ? `${columnWidths[colIndex] || 200}px` : 'none',
                                            zIndex: (colIndex < 2 && !isNonDesktop) ? 1 : 0,
                                            backgroundColor: rowIndex % 2 === 0 ? theme.palette.background.paper : theme.palette.background.default,
                                            borderRight: `1px solid ${theme.palette.divider}`,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            whiteSpace: 'nowrap'
                                        }}
                                    >
                                        <CellContent content={row[column]} column={column} />
                                    </TableCell>
                                    ))
                                )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {rows.length > rowsPerPage && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        p: 0,
                        m: 0,
                    }}
                >
                    <TablePagination
                        rowsPerPageOptions={[5, 10, 25, { label: 'All', value: -1 }]}
                        count={rows.length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        ActionsComponent={TablePaginationActions}
                        sx={{ '& .MuiTablePagination-toolbar': { pl: 0, pr: 0, m: 0 } }}
                    />
                </Box>
            )}

              {/* Success Snackbar */}
            <Snackbar
                open={downloadSuccess}
                autoHideDuration={6000}
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseSuccess} 
                    severity="success" 
                    sx={{ width: '100%' }}
                    variant="filled"
                >
                    Excel file downloaded successfully! Check your Downloads folder.
                </Alert>
            </Snackbar>

            {/* Error Snackbar */}
            <Snackbar
                open={!!downloadError}
                autoHideDuration={8000}
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert 
                    onClose={handleCloseError} 
                    severity="error" 
                    sx={{ width: '100%' }}
                    variant="filled"
                    action={
                        // Show retry button if not a validation error
                        !downloadError?.includes('required') && !downloadError?.includes('sign in') ? (
                            <Button 
                                color="inherit" 
                                size="small" 
                                onClick={() => {
                                    handleCloseError();
                                    setTimeout(() => handleDownload(), 100); // Small delay to ensure snackbar closes
                                }}
                                disabled={downloadLoading}
                                sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                            >
                                Retry
                            </Button>
                        ) : null
                    }
                >
                    {downloadError}
                </Alert>
            </Snackbar>
        </Paper>
    );
}